import moment from "moment";

import { ROUTE_SCHEDULE } from "../consts/apis";
import { ROBOT_DRIVER_TYPE } from "../consts/driverTypes";
import { COLLECTIONS_PLAN_TYPE } from "../consts/planTypes";
import { ROUTE_STATUS } from "../consts/routeDetails";
import { POSTCODE } from "../consts/layers";

import config from "../utils/config";
import api from "../services/api";

import { getParcels, routeHasTerritory } from "./routes";
import { getSecondsFromMMSS } from "./date";

export const optimiseRoute = async (
  planType,
  collectionType,
  depotCode,
  driver,
  vehicle,
  route,
  routeDate,
  scheduledCollections,
  lookupTables
) => {
  if (!driver || !routeHasTerritory(route, planType, collectionType)) return;

  const deliveryStopTime = getSecondsFromMMSS(driver?.deliveryStopTime);
  const collectionStopTime = getSecondsFromMMSS(driver?.collectionStopTime);
  let maxStopNumber = 0;

  const getLatOrLongValue = (latOrLong) => {
    if (!latOrLong) return null;

    let result = latOrLong;

    if (Math.abs(latOrLong) > 1000000000) {
      result = result / 10000000;
    }

    return result + "";
  };
  let requestStops = route.stops.map((stop) => {
    if (
      typeof stop.stopNumber === "number" &&
      parseInt(stop.stopNumber) > maxStopNumber
    ) {
      maxStopNumber = parseInt(stop.stopNumber);
    }
    return {
      stopNumber: stop.stopNumber + "",
      serviceStartTime: stop.serviceStartTime,
      serviceEndTime: stop.serviceEndTime,
      stopTime:
        stop.stopType === COLLECTIONS_PLAN_TYPE
          ? collectionStopTime
          : deliveryStopTime,
      stopType: stop.stopType,
      priority: stop.priority,
      stopConsolidated: stop.stopConsolidated,
      stopLocation: {
        latitude: getLatOrLongValue(stop.latitude),
        longitude: getLatOrLongValue(stop.longitude),
      },
      postcode: stop.postcode,
    };
  });

  if (config.OPTIMISE_SCHEDULED_COLLECTIONS) {
    (route.scheduledCollections || []).forEach((scheduleCollection) => {
      maxStopNumber++;
      let scheduledCollection =
        scheduledCollections[scheduleCollection.scheduleCollectRefs];
      requestStops.push({
        stopNumber: maxStopNumber + "",
        serviceStartTime: scheduledCollection?.collectReadyTime,
        serviceEndTime: scheduledCollection?.collectCloseTime,
        stopTime: 0,
        stopConsolidated: false,
        stopLocation: {
          latitude:
            Math.trunc(
              ((scheduledCollection?.stopLocation?.coordinates || [])[1] || 0) *
                1000000
            ) + "",
          longitude:
            Math.trunc(
              ((scheduledCollection?.stopLocation?.coordinates || [])[0] || 0) *
                1000000
            ) + "",
        },
        postcode: scheduledCollection.collectionPostcode,
        stopType: "S", // TODO why is that "S" for collections? anyway disabled for now in config OPTIMISE_SCHEDULED_COLLECTIONS
        priority: 0,
      });
    });
  }

  if (requestStops.length > 300) {
    console.error(
      `Too many stops (${route.stops.length}) for route ${route.routeCode}`
    );
  }

  let {
    startPostcode,
    endPostcode,
    startLatitude = null,
    startLongitude = null,
    endLatitude = null,
    endLongitude = null,
  } = driver;

  if (
    startPostcode &&
    (typeof startLatitude !== "number" || typeof startLongitude !== "number")
  )
    [startLongitude, startLatitude] = lookupTables[POSTCODE][startPostcode]
      ?.center || [null, null];

  if (
    endPostcode &&
    (typeof endLatitude !== "number" || typeof endLongitude !== "number")
  )
    [endLongitude, endLatitude] = lookupTables[POSTCODE][startPostcode]
      ?.center || [null, null];

  const { data } = await api.post(
    ROUTE_SCHEDULE(depotCode, route.routeCode),
    null,
    {
      optimise: true,
      routeDate,
      route: {
        routeCode: route.routeCode,
        useTollRoads: !!route.useTollRoads,
        useFerry: !!route.useFerry,
        stops: requestStops,
      },
      driver: {
        driverType: driver.driverType,
        robotTypeCode: driver.robotTypeCode,
        dutyStartTime: driver.dutyStartTime,
        plannedDepartTime: driver.plannedDepartTime,
        plannedReturnTime: driver.plannedReturnTime,
        startAtSatellite: !!driver.startAtSatellite,
        endAtSatellite: !!driver.endAtSatellite,
        startLatitude,
        startLongitude,
        endLatitude,
        endLongitude,
      },
      vehicle: {
        breakTime:
          driver.driverType !== ROBOT_DRIVER_TYPE ? vehicle.breakTime : 0,
        maxDutyTime:
          driver.driverType !== ROBOT_DRIVER_TYPE ? vehicle.maxDutyTime : 86400,
      },
    }
  );

  let exceptionCounts = (data.stop || []).reduce(
    (acc, stop) => {
      if (stop.exception === 1) {
        acc.postcodeExceptions += 1;
      } else if (stop.exception === 2) {
        acc.serviceExceptions += 1;
      } else if (stop.exception === 3) {
        acc.dutyExceptions += 1;
      } else if (stop.exception === 6) {
        acc.rangeExceptions += 1;
      } else if (stop.exception === 7) {
        acc.dutyExceptions += 1;
      }
      return acc;
    },
    {
      postcodeExceptions: 0,
      serviceExceptions: 0,
      dutyExceptions: 0,
      rangeExceptions: 0,
    }
  );

  const returnToDepotHour = parseInt(data.returnToDepot.split(":")[0]);
  const days = returnToDepotHour / 24;
  const returnToDepot =
    returnToDepotHour - Math.floor(days) * 24 + data.returnToDepot.substr(2);
  const returnToDepotMoment = moment(returnToDepot, "HH:mm:ss");
  returnToDepotMoment.add(Math.floor(days), "days");
  let projectedDutyTime = returnToDepotMoment.diff(
    moment(driver.plannedDepartTime, "HH:mm"),
    "minutes"
  );
  let plannedDutyTime = moment(driver.plannedReturnTime, "HH:mm").diff(
    moment(driver.plannedDepartTime, "HH:mm"),
    "minutes"
  );
  let dutyDifference = plannedDutyTime - projectedDutyTime;

  let routeStatus = ROUTE_STATUS.DEFAULT;
  if (dutyDifference >= -45 && dutyDifference <= 45) {
    routeStatus = ROUTE_STATUS.OK;
  } else if (dutyDifference < -45) {
    routeStatus = ROUTE_STATUS.DUTY_EXCEEDED;
  } else if (dutyDifference > 45) {
    routeStatus = ROUTE_STATUS.OPPORTUNITY;
  }

  return {
    dutyDifference,
    routeMileage: data.mileage,
    routeStatus: routeStatus,
    departTime: driver.plannedDepartTime,
    returnTime: data.returnToDepot > "23:59" ? "23:59" : data.returnToDepot,
    numberOfStops: requestStops && requestStops.length,
    numberOfParcels: getParcels(route),
    opportunityTime: dutyDifference > 0 ? dutyDifference : 0,
    scheduleCollections: route.scheduledCollections
      ? route.scheduledCollections.length
      : 0,
    ...exceptionCounts,
  };
};
